import React, { Suspense } from 'react';
import type { ComponentType } from 'react';

import type { ExtensionComponentProps, Parameters } from '@atlaskit/editor-common/extensions';
import { NativeEmbedInitialPlaceholder } from '@atlaskit/native-embeds-common/initial-placeholder';
import type { NativeEmbedParameters } from '@atlaskit/native-embeds-common/types';
import { getParameters } from '@atlaskit/native-embeds-common/utils';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';

import type NativeEmbedUIComponent from './ui';

type NativeEmbedExtensionParams = Parameters & NativeEmbedParameters;
export type NativeEmbedUILoaderProps = React.ComponentProps<typeof NativeEmbedUIComponent>;

const NativeEmbedUI = React.lazy<ComponentType<NativeEmbedUILoaderProps>>(() =>
	import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui').then(
		({ default: NativeEmbedUIComponent }) => ({
			default: NativeEmbedUIComponent,
		}),
	),
);

export const NativeEmbedLoadingPlaceholder = ({
	node,
}: ExtensionComponentProps<NativeEmbedExtensionParams>): React.JSX.Element => {
	const { parameters } = node;
	const { url } = getParameters(parameters);
	const manifest = url ? getNativeEmbedFacade().getManifestByUrl(url) : undefined;

	return <NativeEmbedInitialPlaceholder manifest={manifest} parameters={parameters} />;
};

/**
 * Synchronous shell for the editor extension renderer.
 *
 * Editor's generic async extension loading state renders `null`, which causes the native embed block to
 * collapse before the real UI chunk arrives. This small shell keeps the full UI split into its existing
 * async chunk while providing a dimensionally accurate placeholder immediately.
 */
export default function NativeEmbedUILoader(props: NativeEmbedUILoaderProps): React.JSX.Element {
	return (
		<Suspense fallback={<NativeEmbedLoadingPlaceholder {...props} />}>
			<NativeEmbedUI {...props} />
		</Suspense>
	);
}
